/**
 * Centralized Environment Configuration
 * Replaces all hardcoded URLs with dynamic configuration
 */

type EnvType = "development" | "production";

// Get current environment based on API configuration
export const getCurrentEnvironment = (): EnvType => {
  const isProd =
    API_CONFIG.API_BASE.includes("partner.hbox.ai") || API_CONFIG.GRAPHQL_ENDPOINT.includes("graphql-prod");
  return isProd ? "production" : "development";
};

// App Version Configuration
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || "V 1.2.0";

interface ApiConfig {
  API_BASE: string;
  GRAPHQL_ENDPOINT: string;
  GRAPHQL_WEBSOCKET_URL: string;
  ORIGIN_URL: string;
  REFERER_URL: string;
}

// Environment-aware configuration using Vite environment variables
const getEnvironmentConfig = (): ApiConfig => {
  return {
    API_BASE: import.meta.env.VITE_API_BASE_URL || "https://sandbox.hbox.ai",
    GRAPHQL_ENDPOINT: `${import.meta.env.VITE_GRAPHQL_BASE_URL || "https://graphql-stage.hbox.ai"}/v1/graphql`,
    GRAPHQL_WEBSOCKET_URL: import.meta.env.VITE_GRAPHQL_WEBSOCKET_URL || "wss://graphql-stage.hbox.ai/v1/graphql",
    ORIGIN_URL: import.meta.env.VITE_APP_ORIGIN || "https://sandbox.hbox.ai",
    REFERER_URL: import.meta.env.VITE_APP_REFERER || "https://sandbox.hbox.ai",
  };
};

// Environment settings (legacy support)
const ENVIRONMENTS: Record<EnvType, ApiConfig> = {
  development: {
    API_BASE: "https://sandbox.hbox.ai",
    GRAPHQL_ENDPOINT: "https://graphql-stage.hbox.ai/v1/graphql",
    GRAPHQL_WEBSOCKET_URL: "wss://graphql-stage.hbox.ai/v1/graphql",
    ORIGIN_URL: "https://pep-sandbox.hbox.ai",
    REFERER_URL: "https://pep-sandbox.hbox.ai/",
  },
  production: {
    API_BASE: "https://partner.hbox.ai",
    GRAPHQL_ENDPOINT: "https://graphql-prod.hbox.ai/v1/graphql",
    GRAPHQL_WEBSOCKET_URL: "wss://graphql-prod.hbox.ai/v1/graphql",
    ORIGIN_URL: "https://pep.hbox.ai",
    REFERER_URL: "https://pep.hbox.ai/",
  },
};

// Export environment-aware config (uses Vite env vars with fallbacks)
export const API_CONFIG = getEnvironmentConfig();

// Legacy function for backward compatibility
export const getLegacyEnvironmentConfig = (env: EnvType) => ENVIRONMENTS[env];

// Legacy API endpoints for backward compatibility
export const TWILIO_TOKEN_ENDPOINT = `${API_CONFIG.API_BASE}/twilio/token`;
export const TWILIO_BLIND_TRANSFER_ENDPOINT = `${API_CONFIG.API_BASE}/twilio/transfer`;
export const TWILIO_WARM_TRANSFER_ENDPOINT = `${API_CONFIG.API_BASE}/twilio/warm-transfer`;
export const PEP_V2_API_BASE = 'v2';

// Standard headers factory
export const getStandardHeaders = (includeAuth = false, token?: string) => {
  const headers: Record<string, string> = {
    accept: "*/*",
    "accept-language": "en-US,en;q=0.9",
    "cache-control": "no-cache",
    "content-type": "application/json; charset=utf-8",
    origin: API_CONFIG.ORIGIN_URL,
    pragma: "no-cache",
    priority: "u=1, i",
    referer: API_CONFIG.REFERER_URL,
    "sec-ch-ua": '"Google Chrome";v="141", "Not?A_Brand";v="8", "Chromium";v="141"',
    "sec-ch-ua-mobile": "?0",
    "sec-ch-ua-platform": '"macOS"',
    "sec-fetch-dest": "empty",
    "sec-fetch-mode": "cors",
    "sec-fetch-site": "cross-site",
    "user-agent":
      "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/141.0.0.0 Safari/537.36",
  };

  if (includeAuth && token) {
    headers["authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// GraphQL headers factory
export const getGraphQLHeaders = (hasuraToken: string) => {
  return {
    ...getStandardHeaders(true, hasuraToken),
    priority: "u=1, i",
    "sec-ch-ua": '"Not)A;Brand";v="8", "Chromium";v="138", "Google Chrome";v="138"',
    "sec-ch-ua-mobile": "?0",
    "sec-ch-ua-platform": '"macOS"',
  };
};

// Special clinic IDs used across all environments for patient list queries
export const PATIENT_LIST_CLINIC_IDS = [67, 63, 68, 34, 71, 48] as const;

export default API_CONFIG;
